<div class="dash-shell ks-create">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/dashboard">Home</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      @if (entity(); as e) {
        <a class="page-head__crumb" [routerLink]="['/entity', e.short_id, 'dashboard']">{{
          e.name
        }}</a>
        <span class="page-head__sep" aria-hidden="true">/</span>
      }
      <a class="page-head__crumb" [routerLink]="['/entity', entityId, 'kickstart']"
        >Kickstart Cash</a
      >
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page">Detail</span>
    </nav>
    <div class="page-head__bar">
      <div class="page-head__titles page-head__titles--icon">
        <span class="page-head__icon" aria-hidden="true"><app-icon name="zap" [size]="24" /></span>
        <h1 class="page-head__title">Kickstart Cash</h1>
        @if (application(); as app) {
          @if (hasConfirmedAmount(app)) {
            <p class="page-head__subtitle">Applied for {{ app.amount_requested | zar }}</p>
          } @else {
            <p class="page-head__subtitle" data-testid="amount-not-confirmed">
              Amount not yet confirmed
            </p>
          }
          @if (app.dealt_with_by) {
            <p class="page-head__subtitle" data-testid="dealt-with-by">
              Dealt with at CapX by {{ app.dealt_with_by }}
            </p>
          }
        }
      </div>
      @if (application(); as app) {
        <div class="page-head__bar-right">
          <div class="page-head__actions">
            <app-status-chip [status]="app.ks_status" />
            <app-status-chip [status]="app.application_stage" kind="ks-stage" />
            @if (app.stage_row?.path; as path) {
              <span class="ks-path-stage" data-testid="path-stage">{{ path.label }}</span>
            }
          </div>
          @if (isDraft()) {
            <a
              mat-flat-button
              color="primary"
              href="#capture-form"
              aria-label="Continue completing your Kickstart Cash application"
              data-testid="resume-draft-btn"
            >
              <app-icon name="arrow-right" [size]="18" />
              <span>Continue application</span>
            </a>
          }
        </div>
      }
    </div>
  </header>

  @if (isLoading()) {
    <div class="loading-state" aria-label="Loading application">
      <span class="sg-skeleton sg-skeleton--heading sg-skeleton--w40"></span>
      <span class="sg-skeleton sg-skeleton--text" style="width: 25%"></span>
      <mat-card appearance="outlined" style="margin-top: var(--space-6)">
        <mat-card-content>
          <span
            class="sg-skeleton sg-skeleton--text"
            style="width: 30%; margin-bottom: var(--space-4); display: block"
          ></span>
          <span class="sg-skeleton sg-skeleton--text sg-skeleton--w100"></span>
          <span class="sg-skeleton sg-skeleton--text sg-skeleton--w80"></span>
          <span class="sg-skeleton sg-skeleton--text sg-skeleton--w60"></span>
          <span class="sg-skeleton sg-skeleton--text sg-skeleton--w70"></span>
        </mat-card-content>
      </mat-card>
    </div>
  } @else if (loadError()) {
    <div class="sg-empty sg-empty--error sg-empty--bordered">
      <app-icon name="x-circle" [size]="40" />
      <p class="sg-empty__title">Couldn't load this application</p>
      <p class="sg-empty__desc">Something went wrong on our side.</p>
      <button mat-stroked-button type="button" (click)="reload()">
        <app-icon name="refresh" [size]="18" /> Try again
      </button>
    </div>
  } @else if (application(); as app) {
    @if (serverError(); as error) {
      <div class="ks-banner ks-banner--error" role="alert">
        <app-icon name="x-circle" [size]="18" />
        <span>{{ error }}</span>
      </div>
    }

    @if (isDraft()) {
      <div class="draft-banner" role="note">
        <app-icon name="pencil" [size]="18" aria-hidden="true" />
        <p class="draft-banner__text" data-testid="draft-banner-text">
          Started {{ app.created_at | ago }} — still a draft. Fill in your details and upload your
          documents, then submit when you're ready.
        </p>
      </div>
    }

    <!-- FLOW-402 (DOC-89-A §5): the stage row, from the record — the same row
         Ops and the agent see. Replaces the four-step journey; no percentage
         and no ending here: the status chip and the offer notices say what
         happened, in their own words. -->
    @if (!isDraft()) {
      <app-stage-row class="ks-stage" [row]="app.stage_row" />
    }

    <!-- Assessment progress (BRE-200 Screen 6.2) — under review only.
         A rolled-up percentage; the individual onboarding conditions are Ops
         detail and never surface to the client (N4). -->
    @if (showOnboardingProgress()) {
      <mat-card appearance="outlined" class="ks-card">
        <mat-card-content>
          <div class="ks-progress">
            <div class="ks-progress__head">
              <h2 class="ks-card__title">Assessment</h2>
              <span class="ks-progress__pct" data-testid="onboarding-pct"
                >{{ onboardingPercent() }}% complete</span
              >
            </div>
            <mat-progress-bar
              mode="determinate"
              [value]="onboardingPercent()"
              aria-label="Assessment completeness"
            />
            <p class="ks-progress__note">
              <!-- DOC-73 UI-DA-73-03: the back end's position, never fixed text.
                   It leads: after acceptance the back end reports one only while
                   an account the business named since waits (DOC-86 UI-DA-86-05). -->
              @if (progress(); as line) {
                <span data-testid="progress-line">{{ line }}</span>
              } @else if (showConfirmations() && !allConfirmed()) {
                We're reviewing your application. This updates as each check clears. Please complete
                the confirmations below.
              } @else if (offerState() === 'OPEN') {
                Your offer is ready — please review it below.
              }
            </p>
          </div>
        </mat-card-content>
      </mat-card>
    }

    <!-- DOC-73 UI-DA-73-02 (FLOW-326) — what is still outstanding and who owes
         it, from the back end, directly under the status. Each panel renders
         only with items; outside the window the list is empty. -->
    @if (app.outstanding?.length || app.tax_pins?.length) {
      <app-ks-outstanding
        [shortId]="app.short_id"
        [entityShortId]="entityId"
        [items]="app.outstanding"
        [taxPins]="app.tax_pins ?? []"
        [taxPinOpen]="app.tax_pin_open"
        (changed)="onOutstandingChanged()"
      />
    }

    <!-- FLOW-224 (DOC-78) — the first look, or the processing line; the offer
         takes its place once issued. The back end decides; this works nothing out. -->
    @if (showFirstLook()) {
      <app-first-look
        [firstLook]="app.first_look"
        [amountRequested]="app.amount_requested"
        [offerIssued]="!!offer()"
      />
    }

    <!-- Screen 5.0 — the Kickstart offer (FLOW-303, DOC-62 §4–§5).
         The card shows the offer record's figures and computes none of them.
         No confirmations, signature, bank account or mandate appear here in any
         state. Client wording is PROVISIONAL (DOC-62 §9). -->
    @if (offer(); as o) {
      <mat-card appearance="outlined" class="ks-card" data-testid="offer-section">
        <mat-card-content>
          <div class="ks-card__head">
            <h2 class="ks-card__title">Your offer</h2>
            <span class="ks-offer__badge" data-testid="offer-badge">
              @switch (o.state) {
                @case ('OPEN') {
                  @if (offerChanged()) {
                    Updated offer — please review
                  } @else if (daysLeft() !== null) {
                    Offer open until {{ o.expires_at | date: 'd MMMM y' }} — {{ daysLeft() }}
                    {{ daysLeft() === 1 ? 'day' : 'days' }} left
                  } @else if (o.expires_at) {
                    Offer — open until {{ o.expires_at | date: 'd MMMM y' }}
                  } @else {
                    Offer open
                  }
                }
                @case ('ACCEPTED') {
                  Offer accepted
                }
                @case ('LAPSED') {
                  Offer expired
                }
                @case ('WITHDRAWN') {
                  Offer withdrawn
                }
              }
            </span>
          </div>

          @if (o.state === 'LAPSED') {
            <!-- DOC-62 §5.4 — neutral: time ran out, not a decision. -->
            <p class="ks-offer__notice" data-testid="offer-lapsed">
              This offer was open until {{ o.expires_at | date: 'd MMMM y' }} and has now expired.
              That's not a decision on your application — if you'd still like to go ahead, contact
              us and we can look at issuing a fresh offer.
            </p>
          } @else if (o.state === 'WITHDRAWN') {
            <p class="ks-offer__notice" data-testid="offer-withdrawn">
              We've withdrawn this offer. We'll be in touch about next steps.
            </p>
          } @else {
            <app-ks-offer-card [offer]="o" />

            @if (o.state === 'ACCEPTED') {
              <p class="ks-offer__accepted" data-testid="offer-accepted-by">
                Accepted by {{ o.accepted_by_name }} on {{ o.accepted_at | date: 'd MMMM y' }}
              </p>
              @if (showConfirmations()) {
                <a mat-stroked-button href="#confirmations">Continue to sign and confirm</a>
              }
            } @else {
              <!-- DOC-62 §4.2 — what accepting does and does not do, above the control. -->
              <p class="ks-offer__meaning">
                Accepting tells us you're happy with these terms. It is not a loan agreement and
                doesn't commit you. Next you'll sign the agreement, confirm your debit order and
                your payout account. The agreement will match these terms exactly. If we need to
                change any term before then, we'll show you the new offer and ask you to accept
                again.
              </p>
              <div class="form__actions">
                <button
                  mat-flat-button
                  color="primary"
                  type="button"
                  [disabled]="isAccepting()"
                  data-testid="accept-offer-btn"
                  (click)="acceptOffer()"
                >
                  {{ isAccepting() ? 'Accepting…' : 'Accept offer' }}
                </button>
              </div>

              <!-- DOC-62 §4.4 — WEEKLY only: a request, never a choice. -->
              @if (o.repayment_frequency === 'WEEKLY') {
                @if (o.monthly_request_pending) {
                  <p class="ks-offer__notice" data-testid="monthly-request-pending">
                    Your request to pay monthly is with us. This offer stays open meanwhile.
                  </p>
                } @else if (showMonthlyForm()) {
                  <div class="ks-offer__request" data-testid="monthly-request-form">
                    <mat-form-field appearance="outline">
                      <mat-label>Why would monthly suit your business better?</mat-label>
                      <textarea matInput rows="3" [formControl]="monthlyReason"></textarea>
                    </mat-form-field>
                    <div class="form__actions">
                      <button
                        mat-stroked-button
                        type="button"
                        [disabled]="monthlyReason.invalid || isRequestingMonthly()"
                        (click)="requestMonthly()"
                      >
                        {{ isRequestingMonthly() ? 'Sending…' : 'Send request' }}
                      </button>
                      <button mat-button type="button" (click)="showMonthlyForm.set(false)">
                        Cancel
                      </button>
                    </div>
                  </div>
                } @else {
                  <button
                    mat-button
                    type="button"
                    class="ks-offer__link"
                    data-testid="ask-monthly-link"
                    (click)="showMonthlyForm.set(true)"
                  >
                    Ask to pay monthly instead
                  </button>
                }
              }
            }
          }
          <p class="ks-offer__questions">Questions about this offer? Contact your CapX team.</p>
        </mat-card-content>
      </mat-card>
    }

    <!-- Client confirmations (BRE-200 KS-OB-06/07/08).
         Each is a hard gate on activation: until all three are confirmed — or
         Ops records a waiver — the application cannot go live. -->
    @if (showConfirmations()) {
      <mat-card appearance="outlined" class="ks-card" id="confirmations">
        <mat-card-content>
          <div class="ks-card__head">
            <h2 class="ks-card__title">Your confirmations</h2>
          </div>
          <p class="ks-confirm__intro">
            @if (allConfirmed()) {
              Thank you. We'll send your documents for signing next.
            } @else {
              Before your facility can go live, please confirm each of the following.
            }
          </p>

          <!-- DOC-86 (FLOW-366) — the business confirms its own account list,
               first among the confirmations; never on an agent's view. -->
          @if (accountStep(); as step) {
            <app-ks-account-step
              [shortId]="app.short_id"
              [step]="step"
              [confirmation]="app.account_confirmation"
              (changed)="onOutstandingChanged()"
            />
          }

          <ul class="ks-confirm__list">
            @for (item of confirmations(); track item.key) {
              <li class="ks-confirm__item" [attr.data-testid]="'confirm-' + item.key">
                <div class="ks-confirm__text">
                  <span class="ks-confirm__label">{{ item.label }}</span>
                  <span class="ks-confirm__desc">{{ item.description }}</span>
                </div>

                @if (item.done) {
                  <!-- Icon + text, never colour alone (a11y). -->
                  <span class="ks-confirm__done" [attr.data-testid]="'confirmed-' + item.key">
                    <app-icon name="check" [size]="16" />
                    @if (item.key === 'loan_agreement') {
                      <span>Signed {{ item.at | date: 'd MMM y' | dash }}</span>
                    } @else {
                      <span>Confirmed {{ item.at | date: 'medium' | dash }}</span>
                    }
                  </span>
                } @else if (item.clientAction) {
                  <button
                    mat-stroked-button
                    type="button"
                    [disabled]="confirmingKey() !== null"
                    [attr.data-testid]="'confirm-btn-' + item.key"
                    (click)="confirm(item)"
                  >
                    {{ confirmingKey() === item.key ? 'Confirming…' : item.action }}
                  </button>
                }
              </li>
            }
          </ul>
        </mat-card-content>
      </mat-card>
    }

    <!-- Application details -->
    <mat-card appearance="outlined" class="ks-card">
      <mat-card-content>
        <div class="ks-card__head">
          <h2 class="ks-card__title">Application details</h2>
        </div>
        <dl class="facts">
          <div class="facts__row">
            <dt>Business</dt>
            <dd>{{ app.entity_name }}</dd>
          </div>
          <div class="facts__row">
            <dt>Purpose</dt>
            <dd>{{ app.purpose | dash }}</dd>
          </div>
          <div class="facts__row">
            <dt>Submitted</dt>
            <dd>
              {{ app.submitted_at | date: 'medium' | dash }}
            </dd>
          </div>
        </dl>
      </mat-card-content>
    </mat-card>

    <!-- Your facility (FLOW-119) — shown once activated.
         ⚠️ The charge is a FACILITY FEE, never interest: BRE-056 §3 makes it a
         fixed fee struck once on the loan amount, and neither CapX product
         charges interest. Do not relabel any of this. -->
    @if (isActive() && facility(); as f) {
      <mat-card appearance="outlined" class="ks-card" data-testid="facility-card">
        <mat-card-content>
          <div class="ks-card__head">
            <h2 class="ks-card__title">Your facility</h2>
          </div>
          <dl class="facts">
            <div class="facts__row">
              <dt>Amount advanced</dt>
              <dd>{{ f.approved_amount | zar }}</dd>
            </div>
            <div class="facts__row">
              <dt>Facility fee</dt>
              <dd>{{ f.facility_fee_amount | zar }} ({{ f.fee_rate_applied }}%)</dd>
            </div>
            <div class="facts__row">
              <dt>Total repayable</dt>
              <dd>{{ f.total_repayable | zar }}</dd>
            </div>
            <div class="facts__row">
              <dt>Instalments</dt>
              <dd>
                {{ f.instalment_count }}
                {{ f.repayment_frequency === 'WEEKLY' ? 'weekly' : 'monthly' }}
                @if (instalmentAmount()) {
                  <span>of {{ instalmentAmount() | zar }}</span>
                }
              </dd>
            </div>
            <div class="facts__row">
              <dt>Next instalment</dt>
              <dd data-testid="next-instalment">
                @if (f.next_instalment; as next) {
                  {{ next.amount | zar }} due {{ next.due_date | date: 'mediumDate' }}
                } @else {
                  —
                }
              </dd>
            </div>
          </dl>
        </mat-card-content>
      </mat-card>

      <!-- Repayment schedule. Every instalment is equal except the last, which
           carries the rounding remainder so the schedule sums exactly. -->
      <mat-card appearance="outlined" class="ks-card" data-testid="schedule-card">
        <mat-card-content>
          <div class="ks-card__head">
            <h2 class="ks-card__title">Repayment schedule</h2>
          </div>
          @if (f.instalments.length) {
            <table mat-table [dataSource]="f.instalments" class="ks-table">
              <ng-container matColumnDef="sequence">
                <th mat-header-cell *matHeaderCellDef>#</th>
                <td mat-cell *matCellDef="let row">{{ row.sequence }}</td>
              </ng-container>
              <ng-container matColumnDef="due_date">
                <th mat-header-cell *matHeaderCellDef>Due</th>
                <td mat-cell *matCellDef="let row">{{ row.due_date | date: 'mediumDate' }}</td>
              </ng-container>
              <ng-container matColumnDef="amount">
                <th mat-header-cell *matHeaderCellDef>Amount</th>
                <td mat-cell *matCellDef="let row">{{ row.amount | zar }}</td>
              </ng-container>
              <tr mat-header-row *matHeaderRowDef="scheduleColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: scheduleColumns"></tr>
            </table>
          } @else {
            <div class="sg-empty sg-empty--bordered">
              <p>Your schedule is being prepared. Please check back shortly.</p>
            </div>
          }
        </mat-card-content>
      </mat-card>
    }

    <!-- Capture form — shown when in DRAFT or MORE_INFO_REQUIRED stage -->
    @if (canCapture()) {
      <mat-card appearance="outlined" id="capture-form" class="ks-card">
        <mat-card-content>
          <div class="ks-card__head">
            <h2 class="ks-card__title">Financial details</h2>
            <p class="ks-card__hint">
              Add what you can now — you can save and come back to the rest before you submit. CapX
              verifies your business details for you; you don't need to do anything here.
            </p>
          </div>
          <form [formGroup]="captureForm" (ngSubmit)="saveCapture()" class="form">
            <mat-form-field appearance="outline">
              <mat-label>Trading history (months)</mat-label>
              <input matInput type="number" formControlName="trading_history_months" min="0" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Average monthly turnover (ZAR)</mat-label>
              <input matInput formControlName="monthly_turnover_avg" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Average monthly expenses (ZAR)</mat-label>
              <input matInput formControlName="monthly_expenses_avg" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Surplus cash (ZAR)</mat-label>
              <input matInput formControlName="surplus_cash" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Overdraft facility (ZAR)</mat-label>
              <input matInput formControlName="overdraft_amount" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Director credit score</mat-label>
              <input matInput type="number" formControlName="director_credit_score" min="0" />
            </mat-form-field>

            <!-- §B11.1g — a preference the person deciding sees, never a choice
                 (FLOW-305). The frequency on the offer is set from the statements. -->
            <mat-form-field appearance="outline">
              <mat-label>How often would you prefer to repay?</mat-label>
              <mat-select
                formControlName="repayment_frequency_preference"
                data-testid="frequency-preference"
              >
                <mat-option value="">No preference</mat-option>
                <mat-option value="MONTHLY">Monthly</mat-option>
                <mat-option value="WEEKLY">Weekly</mat-option>
              </mat-select>
              <mat-hint>
                A preference only — how often you repay is set from your bank statements.
              </mat-hint>
            </mat-form-field>

            <div class="form__actions">
              <button
                mat-stroked-button
                type="submit"
                [disabled]="isSaving()"
                data-testid="save-capture-btn"
              >
                @if (isSaving()) {
                  <mat-spinner diameter="20" />
                } @else {
                  <span>Save details</span>
                }
              </button>
            </div>
          </form>
        </mat-card-content>
      </mat-card>
    }

    <!-- Documents -->
    <mat-card appearance="outlined" class="ks-card">
      <mat-card-content>
        <div class="ks-card__head">
          <h2 class="ks-card__title">Supporting documents</h2>
          @if (uploadsNeedConsent()) {
            <!-- DOC-72 UI-DA-72-01: the consent question comes first; no drop target until it is given. -->
            <p class="ks-card__hint" data-testid="uploads-need-consent">
              {{ UPLOADS_NEED_CONSENT }}
            </p>
          } @else if (canCapture()) {
            <p class="ks-card__hint">
              Drop files into the blocks below — you can add several at once. We check each one as
              it uploads and give you a quick signal on whether it looks right (max 100&nbsp;MB
              each, PDF or images).
            </p>
          }
        </div>

        <app-doc-upload-panel
          [docTypes]="docTypes()"
          [shortId]="app.short_id"
          [editable]="canCapture() && !uploadsNeedConsent()"
          [initialDocs]="initialDocs()"
          [uploadFn]="uploadDoc"
          [deleteFn]="deleteDoc"
          [fetchDocsFn]="fetchDocs"
          (inFlightChange)="anyInFlight.set($event)"
        />
      </mat-card-content>
    </mat-card>

    <!-- DOC-73 UI-DA-73-04 — *Tell us about your business* stays open after
         submission until the offer is issued (the back end's
         client_context_open); after that what was added stays readable. A
         DRAFT has it in the application form instead. Client context — never
         evidence: nothing in it reaches an assessment. -->
    @if (!isDraft() && (app.client_context_open || app.client_context.length > 0)) {
      <mat-card appearance="outlined" class="ks-card" data-testid="context-card">
        <mat-card-content>
          <app-ks-context-block
            [shortId]="app.short_id"
            [initialEntries]="app.client_context"
            [open]="app.client_context_open"
          />
        </mat-card-content>
      </mat-card>
    }

    <!-- DOC-89 §4 (FLOW-391): the shared context blocks — what CapX has filed
         on this application, and Add to this. Nothing here asks for anything;
         no count, no state. The internal block never reaches this page. -->
    @if (!isDraft()) {
      <mat-card appearance="outlined" class="ks-card" data-testid="context-blocks-card">
        <mat-card-content>
          <app-context-blocks-client subjectKind="KS_APPLICATION" [subjectId]="app.short_id" />
        </mat-card-content>
      </mat-card>
    }

    <!--
      The declared lists (FLOW-243). The client's own statements about the
      business: other funders it uses, related businesses and people, and its
      directors. Entity-level, so what is declared here is declared for every
      application this business makes.
    -->
    <app-declarations-panel [entityShortId]="entityId" />

    <!--
      A DRAFT is finished in the application form, which asks the questions
      submit needs (DOC-72, FLOW-331): the business's AI consent, the account
      confirmation, at least one bank statement.
    -->
    @if (isDraft()) {
      <div class="ks-actions ks-actions--end">
        <button
          mat-button
          type="button"
          class="ks-actions__withdraw"
          (click)="withdrawDraft()"
          [disabled]="isWithdrawing()"
          data-testid="withdraw-btn"
        >
          {{ isWithdrawing() ? 'Withdrawing…' : 'Withdraw application' }}
        </button>
        <a
          mat-flat-button
          color="primary"
          [routerLink]="['/entity', entityId, 'kickstart', 'new']"
          [queryParams]="{ draft: app.short_id }"
          data-testid="continue-application"
        >
          <span>Continue your application</span>
          <app-icon name="arrow-right" [size]="18" />
        </a>
      </div>
    } @else if (canSubmit()) {
      <!--
        Sign-up question since 2026-09-22. Shown here only as the fallback: no
        standing consent, or the consent on this application was withdrawn.
      -->
      @if (showAiConsentBox()) {
        <div class="ks-ai-consent">
          <app-ai-consent-checkbox [checked]="aiConsent()" (changed)="aiConsent.set($event)" />
        </div>
      }
      <!--
        FLOW-290 — the promise ("you can withdraw at any time") now lives inside
        the control that honours it, so the sentence cannot outlive the action
        again. On a draft there is no consent yet and this renders nothing.
      -->
      <app-ai-consent-withdraw
        [state]="aiConsentState()"
        [confirm]="withdrawAiConsent"
        (withdrawn)="onAiConsentWithdrawn()"
      />
      <div class="ks-actions ks-actions--end">
        <button
          mat-flat-button
          color="primary"
          type="button"
          (click)="submitOrRespond()"
          [disabled]="isSubmitting() || anyInFlight() || !aiConsentOk()"
          data-testid="submit-btn"
        >
          @if (isSubmitting()) {
            <mat-spinner diameter="20" />
          } @else if (anyInFlight()) {
            <mat-spinner diameter="20" />
            <span>Checking documents…</span>
          } @else {
            <app-icon name="check" [size]="18" />
            <span>Respond with updated information</span>
          }
        </button>
      </div>
    }
  }
</div>
